import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { fetchAllTests } from "../../../services/mongod.data";

// Async Thunk — Fetch Automated Tests
export const fetchTests = createAsyncThunk(
  "abtest/fetchTests",
  async ({ cf_username, forceRefresh = false }, { getState, rejectWithValue }) => {
    const { tests, lastFetched } = getState().abtest;

    console.log("ABTest: Starting fetchTests thunk...", { cf_username, forceRefresh });
    // Cache check
    if (tests.length > 0 && lastFetched && !forceRefresh) {
      console.log("Redux: Using cached tests");
      return { fromCache: true, tests };
    }

    if (!cf_username) {
      return rejectWithValue("CF username is required");
    }

    try {
      const data = await fetchAllTests(cf_username)
      if (!data.success) {
        throw new Error(data.error || "Failed to fetch tests");
      }

      return { fromCache: false, tests: data.tests };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk — Fetch Manually Deployed Tests from Firebase
export const fetchManuallyDeployedTests = createAsyncThunk(
  "abtest/fetchManuallyDeployedTests",
  async ({ cf_username, forceRefresh = false }, { getState, rejectWithValue }) => {
    const { manuallyDeployedTests, lastFetchedManualTests } = getState().abtest;

    console.log("ABTest: Starting fetchManuallyDeployedTests thunk...", { cf_username, forceRefresh });
    // Cache check
    if (manuallyDeployedTests.length > 0 && lastFetchedManualTests && !forceRefresh) {
      console.log("Redux: Using cached manually deployed tests");
      return { fromCache: true, tests: manuallyDeployedTests };
    }

    if (!cf_username) {
      return rejectWithValue("CF username is required");
    }

    try {
      const response = await fetch(
        `https://causalfunnel-21-1test.firebaseio.com/${cf_username}/test.json?shallow=true`
      );
      const data = await response.json();
      console.log('Manually deployed tests data:', data);
      const testNames = Object.keys(data || {});
      return { fromCache: false, tests: testNames };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk — Update Manually Deployed Test Status
export const updateManualTestStatus = createAsyncThunk(
  "abtest/updateManualTestStatus",
  async ({ clientName, testName, newStatus }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${testName}/meta_data.json`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update test status: ${response.statusText}`);
      }

      console.log(`✅ Test ${testName} status updated to ${newStatus}`);
      return { testName, newStatus };
    } catch (err) {
      console.error('❌ Error updating test status:', err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk — Fetch Analytics Data for a specific test
export const fetchAnalyticsData = createAsyncThunk(
  "abtest/fetchAnalyticsData",
  async ({ clientName, testName, startDate, endDate }, { getState, dispatch, rejectWithValue }) => {
    const rawCacheKey = `${clientName}/${testName}`;
    const filteredCacheKey = `${clientName}/${testName}/${startDate || "all"}/${endDate || "all"}`;
    const { analyticsCache } = getState().abtest;

    console.log(`ABTest: Starting fetchAnalyticsData thunk for ${testName}...`);

    // 1. If exact filtered result already exists, use it
    if (analyticsCache[filteredCacheKey]?.data?.summary) {
      console.log(`Redux: Using filtered cached analytics for ${filteredCacheKey}`);
      return {
        fromCache: true,
        cacheKey: filteredCacheKey,
        analyticsData: analyticsCache[filteredCacheKey].data,
      };
    }

    let rawEntry = analyticsCache[rawCacheKey]?.data;
    let metaData;
    let processedData;

    try {
      // 2. If raw data already cached, do not call API again
      if (rawEntry?.processedData && rawEntry?.metaData) {
        console.log(`Redux: Using raw cached data for ${rawCacheKey}`);
        metaData = rawEntry.metaData;
        processedData = rawEntry.processedData;
      } else {
        // 3. First time only: fetch from Firebase
        const metaDataResponse = await fetch(
          `https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${testName}/meta_data.json`
        );
        metaData = await metaDataResponse.json();

        const eventDataResponse = await fetch(
          `https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${testName}.json`
        );
        const rawData = await eventDataResponse.json();

        processedData = {};
        processedData[testName] = {};

        if (rawData) {
          Object.entries(rawData).forEach(([eventId, eventItem]) => {
            const { date, event, group, time } = eventItem || {};

            if (!date || !event || !group || !time) return;
            if (!["A", "B"].includes(group)) return;

            if (!processedData[testName][date]) {
              processedData[testName][date] = {
                A: { views: [], clicks: [] },
                B: { views: [], clicks: [] },
              };
            }

            if (event === "view") {
              processedData[testName][date][group].views.push({
                eventId,
                timestamp: time,
                time: new Date(time * 1000).toISOString(),
              });
            } else if (event === "click") {
              processedData[testName][date][group].clicks.push({
                eventId,
                timestamp: time,
                time: new Date(time * 1000).toISOString(),
              });
            }
          });

          Object.keys(processedData[testName]).forEach((date) => {
            ["A", "B"].forEach((group) => {
              processedData[testName][date][group].views.sort((a, b) => a.timestamp - b.timestamp);
              processedData[testName][date][group].clicks.sort((a, b) => a.timestamp - b.timestamp);
            });
          });
        }

        // Save raw processed data once per test
        dispatch(
          cacheAnalytics({
            cacheKey: rawCacheKey,
            analyticsData: {
              metaData: metaData || {},
              processedData,
            },
          })
        );
      }

      // 4. Apply date filter locally
      const filteredData = {};
      filteredData[testName] = {};

      Object.entries(processedData?.[testName] || {}).forEach(([date, dayData]) => {
        const dateObj = new Date(date);
        const start = startDate ? new Date(startDate) : new Date("1970-01-01");
        const end = endDate ? new Date(endDate) : new Date("2099-12-31");

        end.setHours(23, 59, 59, 999);

        if (dateObj >= start && dateObj <= end) {
          filteredData[testName][date] = dayData;
        }
      });

      // 5. Build summary
      const summary = {
        totalDays: 0,
        groupA: { views: 0, clicks: 0, conversionRate: 0 },
        groupB: { views: 0, clicks: 0, conversionRate: 0 },
        overallConversionRate: 0,
      };

      const detailedBreakdown = {};

      Object.entries(filteredData[testName]).forEach(([date, dayData]) => {
        summary.totalDays++;

        const aViews = dayData.A.views.length;
        const aClicks = dayData.A.clicks.length;
        const bViews = dayData.B.views.length;
        const bClicks = dayData.B.clicks.length;

        summary.groupA.views += aViews;
        summary.groupA.clicks += aClicks;
        summary.groupB.views += bViews;
        summary.groupB.clicks += bClicks;

        detailedBreakdown[date] = {
          A: {
            views: aViews,
            clicks: aClicks,
            conversionRate: aViews > 0 ? parseFloat(((aClicks / aViews) * 100).toFixed(2)) : 0,
          },
          B: {
            views: bViews,
            clicks: bClicks,
            conversionRate: bViews > 0 ? parseFloat(((bClicks / bViews) * 100).toFixed(2)) : 0,
          },
        };
      });

      summary.groupA.conversionRate =
        summary.groupA.views > 0
          ? parseFloat(((summary.groupA.clicks / summary.groupA.views) * 100).toFixed(2))
          : 0;

      summary.groupB.conversionRate =
        summary.groupB.views > 0
          ? parseFloat(((summary.groupB.clicks / summary.groupB.views) * 100).toFixed(2))
          : 0;

      const totalViews = summary.groupA.views + summary.groupB.views;
      const totalClicks = summary.groupA.clicks + summary.groupB.clicks;

      summary.overallConversionRate =
        totalViews > 0
          ? parseFloat(((totalClicks / totalViews) * 100).toFixed(2))
          : 0;

      const analyticsData = {
        summary,
        detailedBreakdown,
        rawProcessedData: filteredData,
        metaData: metaData || {},
      };

      // 6. Cache filtered result too
      dispatch(
        cacheAnalytics({
          cacheKey: filteredCacheKey,
          analyticsData,
        })
      );

      return {
        fromCache: false,
        cacheKey: filteredCacheKey,
        analyticsData,
      };
    } catch (err) {
      console.error(`❌ Error fetching analytics for ${testName}:`, err);
      return rejectWithValue(err.message);
    }
  }
);

//Helper: filter out valid tests
const filterValidTests = (tests) =>
  tests.filter((t) => t.basicInfo && t._id !== "store_info");

//Initial State
const initialState = {
  tests: [],
  selectedTest: null,
  loading: false,
  error: null,
  lastFetched: null,
  activeTab: 'automated', // Track which tab is selected
  manuallyDeployedTests: [], // Store manually deployed tests
  loadingManualTests: false, // Track loading state for manual tests
  lastFetchedManualTests: null, // Cache timestamp for manual tests
  updatingManualTestStatus: false, // Track if a manual test status update is in progress
  manualTestStatusError: null, // Store error from manual test status update
  analyticsCache: {}, // Cache analytics data by testName (key: "clientName/testName")
  loadingAnalytics: false, // Track if analytics is loading
  analyticsError: null, // Store analytics error
};

// Slice
const abtestSlice = createSlice({
  name: "abtest",
  initialState,
  reducers: {
    setSelectedTest: (state, action) => {
      const testId = action.payload;
      state.selectedTest =
        state.tests.find(
          (t) => t._id === testId || t.metadata?.testId === testId
        ) || null;
    },

    addTest: (state, action) => {
      const test = action.payload;
      if (test.basicInfo && test._id !== "store_info") {
        state.tests.push(test);
      }
    },

    updateTestStatus: (state, action) => {
      const { testId, status } = action.payload;

      state.tests = state.tests.map((test) =>
        test._id === testId || test.metadata?.testId === testId
          ? {
            ...test,
            metadata: {
              ...test.metadata,
              status,
              updatedAt: new Date().toISOString(),
            },
          }
          : test
      );
    },

    updateTest: (state, action) => {
      const { testId, updates } = action.payload;

      state.tests = state.tests.map((test) =>
        test._id === testId || test.metadata?.testId === testId
          ? { ...test, ...updates }
          : test
      );
    },

    removeTest: (state, action) => {
      const testId = action.payload;
      state.tests = state.tests.filter(
        (t) => t._id !== testId && t.metadata?.testId !== testId
      );
    },

    clearTests: (state) => {
      state.tests = [];
      state.selectedTest = null;
      state.lastFetched = null;
      state.error = null;
    },

    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    clearManuallyDeployedTests: (state) => {
      state.manuallyDeployedTests = [];
      state.lastFetchedManualTests = null;
    },

    cacheAnalytics: (state, action) => {
      const { cacheKey, analyticsData } = action.payload;
      state.analyticsCache[cacheKey] = {
        data: analyticsData,
        timestamp: new Date().toISOString(),
      };
    },

    setLoadingAnalytics: (state, action) => {
      state.loadingAnalytics = action.payload;
    },

    setAnalyticsError: (state, action) => {
      state.analyticsError = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTests.fulfilled, (state, action) => {
        state.loading = false;

        if (!action.payload.fromCache) {
          state.tests = filterValidTests(action.payload.tests);
          state.lastFetched = new Date().toISOString();
        }
      })
      .addCase(fetchTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch tests";
      })
      // Handle fetchManuallyDeployedTests
      .addCase(fetchManuallyDeployedTests.pending, (state) => {
        state.loadingManualTests = true;
      })
      .addCase(fetchManuallyDeployedTests.fulfilled, (state, action) => {
        state.loadingManualTests = false;

        if (!action.payload.fromCache) {
          state.manuallyDeployedTests = action.payload.tests;
          state.lastFetchedManualTests = new Date().toISOString();
        }
      })
      .addCase(fetchManuallyDeployedTests.rejected, (state, action) => {
        state.loadingManualTests = false;
        console.error('Error fetching manually deployed tests:', action.payload);
      })
      // Handle updateManualTestStatus
      .addCase(updateManualTestStatus.pending, (state) => {
        state.updatingManualTestStatus = true;
        state.manualTestStatusError = null;
      })
      .addCase(updateManualTestStatus.fulfilled, (state, action) => {
        state.updatingManualTestStatus = false;
        // Successfully updated - no need to update store as we just need the Firebase update
        console.log('✅ Manual test status updated:', action.payload);
      })
      .addCase(updateManualTestStatus.rejected, (state, action) => {
        state.updatingManualTestStatus = false;
        state.manualTestStatusError = action.payload || 'Failed to update test status';
        console.error('❌ Error updating manual test status:', action.payload);
      })
      // Handle fetchAnalyticsData
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.loadingAnalytics = true;
        state.analyticsError = null;
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        // Analytics data cached by the dispatch(cacheAnalytics()) in the thunk
        console.log(`✅ Analytics data loaded for ${action.payload.cacheKey}`);
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.analyticsError = action.payload || 'Failed to fetch analytics data';
        console.error('❌ Error fetching analytics data:', action.payload);
      });
  },
});

// Export actions
export const {
  setSelectedTest,
  addTest,
  updateTestStatus,
  updateTest,
  removeTest,
  clearTests,
  setActiveTab,
  clearManuallyDeployedTests,
  cacheAnalytics,
  setLoadingAnalytics,
  setAnalyticsError,
} = abtestSlice.actions;

export const selectTests = (state) => state.abtest.tests;
export const selectLoading = (state) => state.abtest.loading;
export const selectError = (state) => state.abtest.error;
export const selectSelectedTest = (state) => state.abtest.selectedTest;
export const selectActiveTab = (state) => state.abtest.activeTab;
export const selectManuallyDeployedTests = (state) => state.abtest.manuallyDeployedTests;
export const selectLoadingManualTests = (state) => state.abtest.loadingManualTests;
export const selectUpdatingManualTestStatus = (state) => state.abtest.updatingManualTestStatus;
export const selectManualTestStatusError = (state) => state.abtest.manualTestStatusError;
export const selectAnalyticsCache = (state) => state.abtest.analyticsCache;
export const selectLoadingAnalytics = (state) => state.abtest.loadingAnalytics;
export const selectAnalyticsError = (state) => state.abtest.analyticsError;

// Helper selector to get cached analytics for a specific test
export const selectCachedAnalytics = (clientName, testName, startDate, endDate) =>
  createSelector([selectAnalyticsCache], (cache) => {
    const cacheKey = `${clientName}/${testName}/${startDate || "all"}/${endDate || "all"}`;
    return cache[cacheKey] || null;
  });

export const selectTestsByStatus = (status) =>
  createSelector([selectTests], (tests) =>
    status === "all"
      ? tests
      : tests.filter((t) => t.metadata?.status === status)
  );

export const selectStatusCounts = createSelector([selectTests], (tests) => ({
  all: tests.length,
  active: tests.filter((t) => t.metadata?.status === "active").length,
  paused: tests.filter((t) => t.metadata?.status === "paused").length,
  stopped: tests.filter((t) => t.metadata?.status === "stopped").length,
  completed: tests.filter((t) => t.metadata?.status === "completed").length,
  draft: tests.filter((t) => t.metadata?.status === "draft").length,
}));

export const selectTestById = (testId) =>
  createSelector([selectTests], (tests) =>
    tests.find((t) => t._id === testId || t.metadata?.testId === testId)
  );

// Reducer
export default abtestSlice.reducer;